import { useState, useEffect, useMemo } from 'react';

import { SelectOption, Account } from '~/shared/utils/types';
import SidePanel from '~/shared/components/SidePanel';
import FilterPanelHeader from '~/shared/components/filters/FilterPanelHeader';

import FilterPanelContent from './FilterPanelContent';

export type GroupFleetManagerSelectionOption = {
  label: string;
  options: SelectOption[];
};

interface AutoscaleRulesFiltersSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResetFilters?: () => void;
  appliedRuleIDFilters: SelectOption[];
  appliedAccountFilters: SelectOption[];
  appliedAutoscaleState: string[];
  onApplyFilters?: (filters: {
    active?: boolean[];
    ruleIds?: string[];
    accountIds?: string[];
  }) => void;
  isOrgView?: boolean;
  accounts?: Account[];
  activeOrgId?: string;
}

export default function AutoscaleRulesFiltersSidePanel({
  open,
  onOpenChange,
  onResetFilters,
  appliedRuleIDFilters,
  appliedAccountFilters,
  appliedAutoscaleState,
  onApplyFilters,
  isOrgView = false,
  accounts = [],
  activeOrgId,
}: AutoscaleRulesFiltersSidePanelProps) {
  const [showActiveRulesOnly, setShowActiveRulesOnly] = useState(false);
  const [pendingRuleIDFilters, setPendingRuleIDFilters] = useState<SelectOption[]>([]);
  const [pendingAccountFilters, setPendingAccountFilters] = useState<SelectOption[]>([]);

  const handleApply = () => {
    const filters: { active?: boolean[]; ruleIds?: string[]; accountIds?: string[] } = {};

    // Only add active filter if toggle is on
    if (showActiveRulesOnly) {
      filters.active = [true];
    }

    // Add rule IDs if any are selected
    if (pendingRuleIDFilters.length > 0) {
      filters.ruleIds = pendingRuleIDFilters.map((option) => option.id);
    }

    // Add account IDs if any are selected
    if (pendingAccountFilters.length > 0) {
      filters.accountIds = pendingAccountFilters.map((option) => option.id);
    }

    onApplyFilters?.(filters);
  };

  const handleClose = () => {
    handleApply();
  };

  // Sync pending state with applied filters when panel opens
  useEffect(() => {
    if (open) {
      setPendingRuleIDFilters(appliedRuleIDFilters);
      setPendingAccountFilters(appliedAccountFilters);
      // Set toggle based on applied state
      setShowActiveRulesOnly(
        appliedAutoscaleState.length === 1 && appliedAutoscaleState[0] === 'active'
      );
    }
  }, [open, appliedRuleIDFilters, appliedAccountFilters, appliedAutoscaleState]);

  const filterableAccounts = useMemo(() => {
    if (!activeOrgId) {
      return accounts;
    }
    return accounts.filter((account) => account.accountId !== activeOrgId);
  }, [accounts, activeOrgId]);

  const handleReset = () => {
    setShowActiveRulesOnly(false);
    setPendingRuleIDFilters([]);
    setPendingAccountFilters([]);
    onResetFilters?.();
    onOpenChange(false);
  };

  const hasActiveFilters =
    showActiveRulesOnly || pendingRuleIDFilters.length > 0 || pendingAccountFilters.length > 0;

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="w-[445px]"
      zIndexClass="z-[120]"
      onClose={handleClose}
    >
      <div className="flex flex-col h-full">
        <FilterPanelHeader
          onClearAll={handleReset}
          hasActiveFilters={hasActiveFilters}
          onClose={() => {
            handleClose();
            onOpenChange(false);
          }}
        />
        <FilterPanelContent
          panelOpen={open}
          pendingAutoscaleState={appliedAutoscaleState}
          setPendingAutoscaleState={() => {}}
          pendingRuleIDFilters={pendingRuleIDFilters}
          setPendingRuleIDFilters={setPendingRuleIDFilters}
          pendingAccountFilters={pendingAccountFilters}
          setPendingAccountFilters={setPendingAccountFilters}
          showActiveRulesOnly={showActiveRulesOnly}
          setShowActiveRulesOnly={setShowActiveRulesOnly}
          isOrgView={isOrgView}
          accounts={filterableAccounts}
        />
      </div>
    </SidePanel>
  );
}
